if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          paginationDots: this.querySelector('.product__media-pagination-dots'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');

        if (this.elements.viewer) {
          this.elements.viewer.addEventListener('slideChanged', (e) => {
            const currentElement = e.detail?.currentElement;
            if (currentElement) this.syncActiveSlide(currentElement);
          });
          this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
          this.elements.viewer.addEventListener('slideChanged', (e) => this.scheduleCenterArrows(e.detail?.currentElement));
        }

        if (this.elements.thumbnails) {
          this.elements.thumbnails.addEventListener('click', (e) => {
            const mediaToSwitch = e.target.closest('[data-target]');
            if (mediaToSwitch) this.setActiveMedia(mediaToSwitch.dataset.target, false);
          });
          if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
        }

        if (this.elements.paginationDots) {
          this.elements.paginationDots.querySelectorAll('.product__media-pagination-dot').forEach((dot) => {
            dot.addEventListener('click', () => {
              const target = dot.getAttribute('data-target');
              if (target) this.setActiveMedia(target, false);
            });
          });
        }

        this._resizeObserver = null;
        this._centerArrowsScheduled = null;
        this._debouncedCenterArrows = debounce((slide) => this.centerArrowsOnActiveSlide(slide), 180);
        window.addEventListener('resize', debounce(() => {
          this.scheduleCenterArrows();
          this.updateMobileSliderHeight();
        }, 120));
      }

      scheduleCenterArrows(activeSlideElement) {
        if (!this.mql.matches || !this.elements.viewer) return;
        const slide = activeSlideElement || this.elements.viewer.querySelector('.product__media-item.is-active') || this.elements.viewer.querySelector('.product__media-item');
        if (slide) this._debouncedCenterArrows(slide);
      }

      connectedCallback() {
        if (this.elements.viewer) {
          if (this._centerArrowsScheduled) clearTimeout(this._centerArrowsScheduled);
          this._centerArrowsScheduled = window.setTimeout(() => {
            this.scheduleCenterArrows();
            this._centerArrowsScheduled = null;
            this.updateMobileSliderHeight();
            const activeSlide =
              this.elements.viewer.querySelector('.product__media-item.is-active') ||
              this.elements.viewer.querySelector('.product__media-item');
            if (activeSlide) this.playActiveMedia(activeSlide);
          }, 200);
        }
      }

      centerArrowsOnActiveSlide(activeSlideElement) {
        if (!this.mql.matches || !this.elements.viewer) return;
        const sliderButtons = this.elements.viewer.querySelector('.product__media-slider-buttons');
        if (!sliderButtons) return;
        const slide = activeSlideElement || this.elements.viewer.querySelector('.product__media-item.is-active') || this.elements.viewer.querySelector('.product__media-item');
        if (!slide) return;

        const viewerRect = this.elements.viewer.getBoundingClientRect();
        const img = slide.querySelector('.product__modal-opener--image img, .deferred-media img, img');
        const rect = img && img.offsetParent ? img.getBoundingClientRect() : slide.getBoundingClientRect();
        const centerY = rect.top - viewerRect.top + rect.height / 2;
        sliderButtons.style.top = centerY + 'px';
        sliderButtons.style.transform = 'translateY(-50%)';

        if (this._resizeObserver) {
          this._resizeObserver.disconnect();
          this._resizeObserver = null;
        }
        this._resizeObserver = new ResizeObserver(() => this._debouncedCenterArrows(slide));
        this._resizeObserver.observe(slide);
        if (img && !img.complete) {
          const onImageLoad = () => {
            img.removeEventListener('load', onImageLoad);
            this._debouncedCenterArrows(slide);
          };
          img.addEventListener('load', onImageLoad);
        }
      }

      syncActiveSlide(currentElement) {
        if (!this.elements.viewer || !currentElement) return;

        this.elements.viewer.querySelectorAll('.product__media-item').forEach((element) => {
          element.classList.toggle('is-active', element === currentElement);
        });

        const mediaId = currentElement.dataset?.mediaId;
        if (!mediaId) return;

        if (this.elements.paginationDots) {
          this.elements.paginationDots.querySelectorAll('.product__media-pagination-dot').forEach((dot) => {
            const isActive = dot.getAttribute('data-target') === mediaId;
            dot.classList.toggle('is-active', isActive);
            dot.setAttribute('aria-current', isActive ? 'true' : 'false');
          });
        }

        this.playActiveMedia(currentElement);
      }

      onSlideChanged(event) {
        const mediaId = event.detail.currentElement?.dataset?.mediaId;
        if (!mediaId) return;

        if (this.elements.thumbnails) {
          const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
          this.setActiveThumbnail(thumbnail);
        }

        if (this.elements.paginationDots) {
          this.elements.paginationDots.querySelectorAll('.product__media-pagination-dot').forEach((dot) => {
            const isActive = dot.getAttribute('data-target') === mediaId;
            dot.classList.toggle('is-active', isActive);
            dot.setAttribute('aria-current', isActive ? 'true' : 'false');
          });
        }

        this.updateMobileSliderHeight(event.detail?.currentElement);
      }

      updateMobileSliderHeight(activeSlide) {
        if (!this.elements.viewer) return;
        if (this.mql.matches) {
          this.elements.viewer.style.height = '';
          return;
        }
        const slide = activeSlide || this.elements.viewer.querySelector('.product__media-item.is-active') || this.elements.viewer.querySelector('.product__media-item');
        if (!slide) return;
        const h = slide.offsetHeight;
        if (h > 0) this.elements.viewer.style.height = h + 'px';
      }

      setActiveMedia(mediaId, prepend, shouldScrollIntoView = true) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.syncActiveSlide(activeMedia);

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        if (shouldScrollIntoView) {
          window.setTimeout(() => {
            if (!this.mql.matches || this.elements.thumbnails) {
              activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft, behavior: 'smooth' });
            }
            const activeMediaRect = activeMedia.getBoundingClientRect();
            // Don't scroll if the image is already in view
            if (activeMediaRect.top > -0.5) return;
            const top = activeMediaRect.top + window.scrollY;
            window.scrollTo({ top: top, behavior: 'smooth' });
          });
        }
        // this.playActiveMedia(activeMedia);
        this.scheduleCenterArrows(activeMedia);
        window.setTimeout(() => this.scheduleCenterArrows(activeMedia), 280);

        if (this.elements.paginationDots) {
          this.elements.paginationDots.querySelectorAll('.product__media-pagination-dot').forEach((dot) => {
            const isActive = dot.getAttribute('data-target') === mediaId;
            dot.classList.toggle('is-active', isActive);
            dot.setAttribute('aria-current', isActive ? 'true' : 'false');
          });
        }

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);

        const item = thumbnail.closest('.thumbnail-list__item');
        const thumbnailList = this.elements.thumbnails.querySelector('.thumbnail-list');
        if (thumbnailList && item) {
          const listRect = thumbnailList.getBoundingClientRect();
          const itemRect = item.getBoundingClientRect();
          const scrollTop = thumbnailList.scrollTop
            + (itemRect.top - listRect.top)
            - (listRect.height / 2)
            + (itemRect.height / 2);
          thumbnailList.scrollTo({ top: scrollTop, behavior: 'smooth' });
        }
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false, { pauseOthers: false });
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}

function getColorFilterFromProductInfo(productInfoElement, variant) {
  if (!productInfoElement) return '';
  const variantSelects = productInfoElement.querySelector('variant-selects');
  if (!variantSelects) return '';

  const groups = variantSelects.querySelectorAll('fieldset, .product-form__input, .select');
  for (let i = 0; i < groups.length; i++) {
    const group = groups[i];
    const labelEl = group.querySelector('legend, label.form__label');
    if (!labelEl) continue;
    const optionName = (labelEl.textContent || '').split(':')[0].trim();
    const optionNameDown = optionName.toLowerCase();
    if (optionNameDown !== 'color' && optionNameDown !== 'colour') continue;

    const checkedRadio = group.querySelector('input[type="radio"]:checked');
    if (checkedRadio) return `${optionName}:${checkedRadio.value}`;

    const select = group.querySelector('select');
    if (select && select.selectedOptions && select.selectedOptions[0]) {
      return `${optionName}:${select.selectedOptions[0].value}`;
    }
  }

  if (variant && variant.option1) return `color:${variant.option1}`;
  return '';
}

function filterByColorValue(value, currentColor) {
  if (!value) return true;
  return value === currentColor || value === 'color:all' || value === '';
}

function hideVariantImages(variantImageFilter, context) {
  if (!context) return;

  const colorFilter = String(variantImageFilter || '').toLowerCase();
  if (!colorFilter) return;

  const viewer = context.querySelector('[id^="Slider-Gallery"]');
  const thumbnails = context.querySelector('[id^="Slider-Thumbnails"]');
  const viewerItems = viewer ? Array.from(viewer.querySelectorAll('.product__media-item')) : [];
  const thumbItems = thumbnails ? Array.from(thumbnails.querySelectorAll('.product-single__thumbnails-item')) : [];
  const isSingleVariant = context.classList.contains('js-product--single-variant');
  const isBundle = context.dataset.productType === 'bundle' || context.classList.contains('product-template-bundle');

  const shouldShow = (el) => {
    if (isBundle || isSingleVariant) return true;
    const color = String(el.dataset.color || '').toLowerCase();
    return filterByColorValue(color, colorFilter);
  };

  const jq = window.jQuery || window.$;
  const hasSlickThumbs = jq && thumbnails && jq(thumbnails).hasClass('slick-initialized');
  const hasSlickViewer = jq && viewer && jq(viewer).hasClass('slick-initialized');

  if (hasSlickThumbs) jq(thumbnails).slick('slickUnfilter');
  if (hasSlickViewer) jq(viewer).slick('slickUnfilter');

  if (hasSlickViewer) {
    jq(viewer).slick('slickFilter', function () {
      return shouldShow(this);
    });
  } else {
    viewerItems.forEach((item) => {
      const visible = shouldShow(item);
      item.style.display = visible ? '' : 'none';
      item.classList.toggle('filtered', visible);
    });
  }

  if (hasSlickThumbs) {
    jq(thumbnails).slick('slickFilter', function () {
      return shouldShow(this);
    });
    jq(thumbnails).find('.product-single__thumbnails-item').each(function (index) {
      this.setAttribute('data-index', index);
    });
  } else {
    thumbItems.forEach((item) => {
      const visible = shouldShow(item);
      item.style.display = visible ? '' : 'none';
      item.classList.toggle('filtered', visible);
    });
    let visibleIndex = 0;
    thumbItems.forEach((item) => {
      if (item.style.display === 'none') return;
      item.setAttribute('data-index', visibleIndex);
      visibleIndex += 1;
    });
  }

  if (isBundle) {
    viewerItems.forEach((item) => {
      item.style.display = '';
      item.classList.add('filtered');
    });
    thumbItems.forEach((item) => {
      item.style.display = '';
      item.classList.add('filtered');
    });
  }

  const mediaGallery = context.querySelector('media-gallery');
  const firstVisibleMedia = viewerItems.find((item) => item.style.display !== 'none');
  if (mediaGallery && firstVisibleMedia && firstVisibleMedia.dataset.mediaId) {
    mediaGallery.setActiveMedia(firstVisibleMedia.dataset.mediaId, false, false);
  }

  const thumbSlider = context.querySelector('#GalleryThumbnails-' + context.dataset.section);
  const viewerSlider = context.querySelector('#GalleryViewer-' + context.dataset.section);
  if (thumbSlider && thumbSlider.resetPages) thumbSlider.resetPages();
  if (viewerSlider && viewerSlider.resetPages) viewerSlider.resetPages();
}

function runVariantImageFilterForProduct(productInfoElement, variant) {
  if (!productInfoElement) return;
  const variantImageFilter = getColorFilterFromProductInfo(productInfoElement, variant);
  if (!variantImageFilter) return;
  hideVariantImages(variantImageFilter, productInfoElement);
}

document.addEventListener('click', (event) => {
  const videoThumb = event.target.closest('.product-single__thumbnails-item[data-video] .product-single__thumbnail');
  if (!videoThumb) return;
  const videoUrl = videoThumb.dataset.video || videoThumb.closest('.product-single__thumbnails-item')?.dataset.video;
  if (!videoUrl) return;
  if (videoUrl.indexOf('#') === 0) return;

  event.preventDefault();
  window.pauseAllMedia?.();
  const win = window.open(videoUrl, '_blank', 'noopener');
  if (win) win.opener = null;
});

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('product-info').forEach((productInfoElement) => {
    const variantScript = productInfoElement.querySelector('variant-selects [data-selected-variant]');
    let variant = null;
    if (variantScript && variantScript.textContent) {
      try {
        variant = JSON.parse(variantScript.textContent);
      } catch (e) {
        variant = null;
      }
    }
    runVariantImageFilterForProduct(productInfoElement, variant);
  });
});

if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
  subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
    const sectionId = data?.sectionId;
    const productInfoElement = sectionId
      ? document.querySelector(`product-info[data-section="${sectionId}"], product-info[data-original-section="${sectionId}"]`)
      : document.querySelector('product-info');
    runVariantImageFilterForProduct(productInfoElement, data?.variant);
  });

  subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, ({ data }) => {
    const source = data?.event?.target;
    const productInfoElement = source ? source.closest('product-info') : null;
    if (!productInfoElement || !source) return;

    const group = source.closest('fieldset, .product-form__input');
    const labelEl = group ? group.querySelector('legend, label.form__label') : null;
    const optionName = (labelEl?.textContent || '').split(':')[0].trim() || 'color';
    const selectedValue = source.tagName === 'SELECT'
      ? source.selectedOptions?.[0]?.value
      : source.value;
    if (!selectedValue) return;
    hideVariantImages(`${optionName}:${selectedValue}`, productInfoElement);
  });
}
